import { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion, useInView } from 'framer-motion';
import {
  Trophy, Dumbbell, Target, Activity, Zap, Baby, Star,
  Clock, ArrowRight, ChevronRight, Mail, User, MapPin, Phone 
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';

const FadeIn = ({ children, delay = 0, className = '' }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-60px' });
  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 20 }}
      animate={isInView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.5, delay, ease: [0.16, 1, 0.3, 1] }}
      className={className}
    >
      {children}
    </motion.div>
  );
};

const Heart = ({ className }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
  </svg>
);

const footballTeams = [
  {
    name: '1. Herrenmannschaft',
    desc: 'Unsere erste Mannschaft kämpft in der Kreisliga. Leidenschaft, Teamgeist und Fußball auf hohem Niveau.',
    contactName: 'Ralf Hansjürgen',
    contact: '+49 170 7368591',
    scheduleUrl: 'https://www.fussball.de/mannschaft/hsc-alswede-hsc-alswede-westfalen/-/saison/2526/team-id/011MIB5G6G000000VTVG0001VTR8C1K7#!/'
  },
  {
    name: '2. Herrenmannschaft',
    desc: 'Nach einjähriger Pause wieder neu gemeldet! Die Zweite ist zurück und hungrig auf Erfolg.',
    contactName: 'André Kirchner',
    contact: '+49 151 40534971',
    scheduleUrl: 'https://www.fussball.de/mannschaft/hsc-alswede-ii-hsc-alswede-westfalen/-/saison/2526/team-id/02TM4IF9MO000000VS5489BSVUCOJR2F#!/'
  },
  {
    name: '3. Herrenmannschaft',
    desc: 'Fußball für alle – hier zählt der Spaß an der Bewegung und am Teamspiel.',
    contactName: 'Oliver Oller',
    contact: '+49 172 2402036',
    scheduleUrl: 'https://www.fussball.de/mannschaft/sg-luebbecke-iv-alswede-iii-fc-luebbecke-westfalen/-/saison/2526/team-id/02Q08KBN8S000000VS5489B1VTF0A3SN#!/'
  },
  {
    name: 'Frauenmannschaft',
    desc: 'Fußball ist nicht nur Männersache! Unsere Frauen spielen begeistert und suchen Verstärkung.',
    contactName: 'Sharon Brown',
    contact: 'sharon.nadine.brown@hsc-alswede.de',
    scheduleUrl: 'https://www.fussball.de/mannschaft/sg-alswede-boerninghausen-hsc-alswede-westfalen/-/saison/2526/team-id/01L7GJ5LAK000000VV0AG80NVT9OCUEA#!/'
  },
  {
    name: 'Altliga Ü32',
    desc: 'Neu in der Altliga! Für alle, die den Fußball nicht missen möchten. Einstieg jederzeit möglich.',
    contactName: 'Ulrich Haseloh',
    contact: 'ulrich.haseloh@hsc-alswede.de',
  },
  {
    name: 'Altliga Ü40 II',
    desc: 'Erfahrene Spieler, große Leidenschaft. Die Ü40 II ist ein Paradebeispiel für Vereinstreue.',
    contactName: 'Ulrich Haseloh',
    contact: 'ulrich.haseloh@hsc-alswede.de',
  },
  {
    name: 'Altliga Ü50',
    desc: 'Weil Fußball keine Altersgrenze hat! Die Ü50 beweist, dass man mit 50+ noch richtig Gas geben kann.',
    contactName: 'Ulrich Haseloh',
    contact: 'ulrich.haseloh@hsc-alswede.de',
  },
];

const breitensportOffers = [
  {
  icon: Dumbbell,
  name: 'Fit am Vormittag',
  time: 'Dienstag & Donnerstag Vormittag',
  location: 'Turnhalle Alswede',
  contactName: 'Petra Mangold',
  contactEmail: 'breitensport@hsc-alswede.de',
  contactPhone: '',
  desc: 'Sanfte Bewegung und Fitness für alle, die tagsüber aktiv bleiben möchten.',
  target: 'Erwachsene / Senioren',
  },
  {
    icon: Zap,
    name: 'Aero Fighting',
    time: 'Wochentags Abend',
    location: 'Turnhalle Alswede',
    contactName: 'Petra Mangold',
    contactEmail: 'breitensport@hsc-alswede.de',
    contactPhone: '',
    desc: 'Kombination aus Aerobic und Kampfsportelementen – effektives Ausdauertraining mit Spaßfaktor.',
    target: 'Alle Altersgruppen',
  },
  {
    icon: Activity,
    name: 'Body-Tone',
    time: 'Wochentags Abend',
    location: 'Turnhalle Alswede',
    contactName: 'Petra Mangold',
    contactEmail: 'breitensport@hsc-alswede.de',
    contactPhone: '',
    desc: 'Kräftigung und Toning des gesamten Körpers. Sanft aber effektiv – du wirst den Unterschied spüren!',
    target: 'Alle Altersgruppen',
  },
  {
    icon: Zap,
    name: 'Badminton',
    time: 'Mittwochs 20:00-21:30 Uhr',
    location: 'Turnhalle Alswede',
    contactName: 'Dirk Thomas',
    contactEmail: 'breitensport@hsc-alswede.de',
    contactPhone: '',
    desc: 'Schnell, reaktionsschnell, spannend. Ob Anfänger oder Fortgeschrittener – Badminton macht süchtig!',
    target: 'Ab 10 Jahren',
  },
  {
    icon: Target,
    name: 'Tischtennis',
    time: 'Donnerstags 19:00-21:30 Uhr',
    location: 'Turnhalle Alswede',
    contactName: 'Carsten Droste',
    contactEmail: 'Carsten.droste@hsc-alswede.de',
    contactPhone: '',
    desc: 'Klein, schnell und unglaublich spaßig. Unser Tischtennis-Team wächst stetig und freut sich über jeden Neuzugang.',
    target: 'Ab 12 Jahren',
  },
  {
    icon: Activity,
    name: 'Volleyball',
    time: 'Dienstags 19:30-21:30 Uhr + Freitags 19:30-21:30 Uhr',
    location: 'Turnhalle Alswede',
    contactName: 'Andrej Harms',
    contactEmail: 'breitensport@hsc-alswede.de',
    contactPhone: '',
    desc: 'Team-Sport pur! Gemeinsam trainieren, aufsteigen, Spaß haben. Volleyball macht aus Fremden Freunde.',
    target: 'Ab 14 Jahren',
  },
  {
    icon: Target,
    name: 'Sportschießen',
    time: 'Montags ab 19 Uhr',
    time: 'Freitags 19:30-21:30 Uhr Alswede',
    location: 'Schießstand am Sportplatz',
    contactName: 'Vorstand HSC Alswede',
    contactEmail: 'breitensport@hsc-alswede.de',
    contactPhone: '',
    desc: 'Konzentration, Ruhe, Präzision: Sportschießen ist eine faszinierende Sportart für alle, die Geduld mitbringen.',
    target: 'Ab 12 Jahren',
  },
  {
    icon: Star,
    name: 'Darts',
    time: 'Montags ab 19 Uhr',
    location: 'Vereinsheim Alswede',
    contactName: 'Yannis Fuchs',
    contactEmail: 'darts@hsc-alswede.de',
    contactPhone: '+49 175 1171245',
    desc: 'Der neueste Hit beim HSC! Darts boomt – und bei uns hast du die Möglichkeit, mitzumachen und erste Turniere zu erleben.',
    target: 'Ab 16 Jahren',
  },
];

const youthOffers = [
  {
    icon: Baby,
    name: 'Mini-Kicker',
    age: '2–6 Jahre',
    time: 'Montag 17:00–18:00 Uhr',
    location: 'Sportplatz Alswede',
    contactName: 'Sebastian Schwarze',
    contactEmail: 'minikicker@hsc-alswede.de',
    contactPhone: '+49 176 55262878',
    desc: 'Beim Training der Minikicker möchten wir den jüngsten Fußballbegeisterten im Alter von 2 bis 6 Jahren ermöglichen, spielerisch an die Sportart herangeführt zu werden. Im Vordergrund steht dabei nicht das Gewinnen, sondern der Spaß an der Bewegung, das gemeinsame Spielen und das Erlernen grundlegender motorischer Fähigkeiten wie Koordination, Schnelligkeit, Gleichgewicht und Ballgefühl. Gleichzeitig fördern wir durch einfache Spiele und Übungen das soziale Miteinander, Teamgeist und Fairness.',
    highlight: 'Kostenlos unter 6',
    color: 'bg-green-100 text-green-700',
  },
  {
    icon: Activity,
    name: 'Kinderturnen',
    age: '4–10 Jahre',
    time: 'Dienstags 17:00–18:00 Uhr',
    location: 'Turnhalle Alswede',
    contactName: 'Claudia Bruels',
    contactEmail: 'claudia.bruels@hsc-alswede.de',
    contactPhone: '+49 151 52248106',
    desc: 'Turnen stärkt nicht nur Körper und Koordination, sondern auch das Selbstvertrauen. Hier lernen Kinder spielerisch ihre körperlichen Fähigkeiten kennen.',
    highlight: null,
    color: 'bg-blue-100 text-blue-700',
  },
  {
    icon: Heart,
    name: 'Eltern-Kind-Turnen',
    age: '1–4 Jahre + Elternteil',
    time: 'Dienstags 16:00-17:00 Uhr',
    location: 'Turnhalle Alswede',
    contactName: 'Claudia Bruels',
    contactEmail: 'claudia.bruels@hsc-alswede.de',
    contactPhone: '+49 151 52248106',
    desc: 'Klettern, Schaukeln, Rennen, Turnen und Toben sind elementare Grundbedürfnisse von Kindern, die sie bei unserem Eltern-Kind-Turnen erfahren! Beim Eltern-Kind-Turnen wollen wir schon den Kleinen ermöglichen sich mit Hilfe ihrer Eltern früh ausprobieren und ihre Körperwahrnehmung zu schulen. Es sollen grundlegende Fähigkeiten erlernt werden, wie Beweglichkeit, Ausdauer, Kraft, Koordination und Schnelligkeit. Dazu bauen wir themenorientierte Bewegungslandschaften auf, die die Kreativität der Kinder anregen und zum Ausprobieren vielfältiger Stationen motivieren. Mit festen Ritualen und kleinen Spielen schulen wird zusätzlich die geistig-kognitive und soziale Kompetenz der Kinder.',
    highlight: 'Familientauglich',
    color: 'bg-purple-100 text-purple-700',
  },
  {
    icon: Baby,
    name: 'Krabbelgruppe',
    age: '0–2 Jahre',
    time: '1. & 3. Samstag im Monat: 10:00 Uhr – 11:00 Uhr',
    location: 'Turnhalle Alswede',
    contactName: 'Claudia Bruels',
    contactEmail: 'claudia.bruels@hsc-alswede.de',
    contactPhone: '+49 151 52248106',
    desc: 'Für die Allerkleinsten! In einer sicheren Umgebung erkunden Babys und Kleinkinder mit ihren Eltern die Welt der Bewegung. Soziale Kontakte inklusive! Was wir anbieten möchten:<br> Gelegenheit Gleichaltrige aus der Umgebung kennenzulernen <br> Freude an Bewegung wecken und ausleben <br> Spielerische Materialerkundung <br> Austauschmöglichkeiten mit anderen Eltern <br> Fließender Übergang zum Kinderturnen Herzlich Willkommen sind alle Kinder ab dem 4. Monat bis zum sicheren Laufen und dessen Eltern (Papas ausdrücklich erwünscht!)',
    highlight: 'Ab Geburt',
    color: 'bg-pink-100 text-pink-700',
  },
];

export default function Sportangebote() {
  useEffect(() => {
    document.title = 'Sportangebote | HSC Alswede von 1946 – Fußball, Breitensport & Jugend';
  }, []);

  return (
    <div className="pt-16">
      {/* Page Hero */}
      <section
        className="relative py-20 lg:py-28 overflow-hidden"
        style={{
          backgroundImage: 'url(https://images.pexels.com/photos/36862523/pexels-photo-36862523.jpeg?auto=compress&cs=tinysrgb&w=1400)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      >
        <div className="absolute inset-0 bg-[#1a1a1a]/70" />
        <div className="relative mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <div className="flex items-center gap-2 text-[#F5C200] text-sm mb-4">
              <Link to="/" className="text-gray-300 hover:text-white transition-colors">Start</Link>
              <ChevronRight className="w-4 h-4" />
              <span>Sportangebote</span>
            </div>
            <h1 className="text-4xl sm:text-5xl font-bold text-white mb-4" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              Sportangebote
            </h1>
            <p className="text-gray-300 max-w-xl">
              Fußball, Breitensport, Kinder & Jugend – für jeden ist beim HSC Alswede etwas dabei.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Tabs */}
      <section className="py-12 lg:py-20">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
          <Tabs defaultValue="fussball" className="w-full">
            <div className="flex justify-center mb-10">
              <TabsList className="bg-gray-100 rounded-xl p-1 h-auto">
                <TabsTrigger
                  value="fussball"
                  className="rounded-lg px-5 py-2.5 text-sm font-medium data-[state=active]:bg-[#F5C200] data-[state=active]:text-[#1a1a1a] data-[state=active]:shadow-none transition-colors"
                >
                  <Trophy className="w-4 h-4 mr-2" />
                  Fußball
                </TabsTrigger>
                <TabsTrigger
                  value="breitensport"
                  className="rounded-lg px-5 py-2.5 text-sm font-medium data-[state=active]:bg-[#F5C200] data-[state=active]:text-[#1a1a1a] data-[state=active]:shadow-none transition-colors"
                >
                  <Dumbbell className="w-4 h-4 mr-2" />
                  Breitensport
                </TabsTrigger>
                <TabsTrigger
                  value="jugend"
                  className="rounded-lg px-5 py-2.5 text-sm font-medium data-[state=active]:bg-[#F5C200] data-[state=active]:text-[#1a1a1a] data-[state=active]:shadow-none transition-colors"
                >
                  <Baby className="w-4 h-4 mr-2" />
                  Jugend & Kids
                </TabsTrigger>
              </TabsList>
            </div>

            {/* Football Tab */}
            <TabsContent value="fussball">
              <FadeIn>
                <div className="text-center mb-10">
                  <h2 className="text-2xl sm:text-3xl font-bold text-[#1a1a1a] mb-3" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                    Fußball beim HSC Alswede
                  </h2>
                  <p className="text-gray-600 max-w-2xl mx-auto">
                    Fußball ist die Herzsparte des HSC Alswede. Mit mehreren Herren-, Frauen- und Altliga-Mannschaften
                    bieten wir Fußball für alle Altersgruppen und Leistungsstufen.
                  </p>
                </div>
              </FadeIn>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {footballTeams.map((team, i) => (
                  <FadeIn key={i} delay={i * 0.07}>
                    <Card className="sport-card rounded-2xl border border-black/[0.08] bg-white p-5 shadow-[0_10px_30px_rgba(17,17,17,0.05)] h-full relative overflow-hidden">
                      <div className="absolute left-0 top-0 h-full w-1 bg-[#F5C200] rounded-l-2xl" />
                      <div className="pl-3">
                        <div className="flex items-center gap-2 mb-3">
                          <div className="w-8 h-8 bg-[#F5C200]/15 rounded-lg flex items-center justify-center">
                            <Trophy className="w-4 h-4 text-[#1a1a1a]" />
                          </div>
                          <h3 className="font-semibold text-[#1a1a1a]" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>{team.name}</h3>
                        </div>
                        <p className="text-sm text-gray-600 leading-relaxed mb-4">{team.desc}</p>
                        <div className="flex items-center gap-3 pt-3 border-t border-gray-100">
                          <div className="flex items-center gap-2">
                            <User className="w-3.5 h-3.5 text-gray-400" />
                            <span className="text-xs font-medium text-gray-700">{team.contactName}</span>
                          </div>
                          <a 
                            href={`mailto:${team.contact}`} 
                            className="flex items-center gap-1 text-xs text-gray-500 hover:text-[#F5C200] transition-colors"
                          >
                            <Mail className="w-3.5 h-3.5" />
                            <span>Kontakt</span>
                          </a>
                          {team.scheduleUrl && (
                              <a
                                href={team.scheduleUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="mt-3 inline-flex items-center gap-2 text-xs font-semibold text-[#1a1a1a] bg-[#F5C200]/20 hover:bg-[#F5C200]/40 px-3 py-1.5 rounded-full transition-colors"
                              >
                                Spielplan ansehen
                                <ArrowRight className="w-3.5 h-3.5" />
                              </a>
                            )}
                        </div>
                      </div>
                    </Card>
                  </FadeIn>
                ))}
              </div>
              <FadeIn>
                <div className="mt-8 p-6 bg-[#FAFAF9] rounded-2xl border border-black/[0.08]">
                  <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                    <div>
                      <h3 className="font-semibold text-[#1a1a1a] mb-1" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>Interesse am Fußball?</h3>
                      <p className="text-sm text-gray-600">Komm einfach zu einem Training vorbei oder schreib uns an.</p>
                    </div>
                    <Link to="/kontakt">
                      <Button className="h-10 px-5 bg-[#F5C200] text-[#1a1a1a] hover:bg-[#E6B400] font-semibold rounded-xl border-0 shadow-none transition-colors flex-shrink-0">
                        Anfragen
                        <ArrowRight className="w-4 h-4 ml-2" />
                      </Button>
                    </Link>
                  </div>
                </div>
              </FadeIn>
            </TabsContent>

            {/* Breitensport Tab */}
            <TabsContent value="breitensport">
              <FadeIn>
                <div className="text-center mb-10">
                  <h2 className="text-2xl sm:text-3xl font-bold text-[#1a1a1a] mb-3" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                    Breitensport – für jeden etwas
                  </h2>
                  <p className="text-gray-600 max-w-2xl mx-auto">
                    Neben Fußball bietet der HSC Alswede ein breites Spektrum an Sportarten.
                    Ob Fitness, Racket-Sport, Präzisionssport oder Spaßsport – hier ist für jeden etwas dabei.
                  </p>
                </div>
              </FadeIn>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {breitensportOffers.map((offer, i) => (
                  <FadeIn key={i} delay={i * 0.07}>
                    <Card className="sport-card rounded-2xl border border-black/[0.08] bg-white p-6 shadow-[0_10px_30px_rgba(17,17,17,0.05)] h-full relative overflow-hidden hover:shadow-[0_16px_45px_rgba(17,17,17,0.10)] transition-shadow">
                      <div className="absolute left-0 top-0 h-full w-1 bg-[#F5C200] rounded-l-2xl" />

                      <div className="pl-3 flex flex-col h-full">
                        <div className="flex items-start gap-4 mb-4">
                          <div className="w-12 h-12 bg-[#F5C200]/15 rounded-2xl flex items-center justify-center flex-shrink-0">
                            <offer.icon className="w-6 h-6 text-[#1a1a1a]" />
                          </div>

                          <div className="flex-1 min-w-0">
                            <div className="flex items-start justify-between gap-2 mb-1">
                              <h3 className="font-bold text-[#1a1a1a]" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                                {offer.name}
                              </h3>
                              <Badge className="text-xs bg-gray-100 text-gray-600 border-0 rounded-full flex-shrink-0">
                                {offer.target}
                              </Badge>
                            </div>

                            <p className="text-sm text-gray-600 leading-relaxed">
                              {offer.desc}
                            </p>
                          </div>
                        </div>

                        <div className="mt-auto pt-4 border-t border-gray-100 space-y-2">
                          {offer.location && (
                            <div className="flex items-center gap-2 text-xs text-gray-500">
                              <MapPin className="w-3.5 h-3.5 text-gray-400" />
                              <span>{offer.location}</span>
                            </div>
                          )}

                          {offer.time && (
                            <div className="flex items-center gap-2 text-xs text-gray-500">
                              <Clock className="w-3.5 h-3.5 text-gray-400" />
                              <span>{offer.time}</span>
                            </div>
                          )}

                          {offer.contactName && (
                            <div className="flex items-center gap-2 text-xs text-gray-700 font-medium">
                              <User className="w-3.5 h-3.5 text-gray-400" />
                              <span>{offer.contactName}</span>
                            </div>
                          )}

                          <div className="flex flex-wrap gap-2 pt-2">
                            {offer.contactEmail && (
                              <a
                                href={`mailto:${offer.contactEmail}`}
                                className="inline-flex items-center gap-1 rounded-full bg-[#F5C200]/15 px-3 py-1.5 text-xs font-medium text-[#1a1a1a] hover:bg-[#F5C200]/30 transition-colors"
                              >
                                <Mail className="w-3.5 h-3.5" />
                                E-Mail
                              </a>
                            )}

                            {offer.contactPhone && (
                              <a
                                href={`tel:${offer.contactPhone.replace(/\s+/g, '')}`}
                                className="inline-flex items-center gap-1 rounded-full bg-gray-100 px-3 py-1.5 text-xs font-medium text-[#1a1a1a] hover:bg-gray-200 transition-colors"
                              >
                                <Phone className="w-3.5 h-3.5" />
                                Anrufen
                              </a>
                            )}
                          </div>
                        </div>
                      </div>
                    </Card>
                  </FadeIn>
                ))}
              </div>
              <FadeIn>
                <div className="mt-8 text-center">
                  <p className="text-sm text-gray-500 mb-4">Fragen zu Trainingszeiten oder Gruppen? Meld dich einfach!</p>
                  <Link to="/kontakt">
                    <Button className="h-10 px-6 bg-[#F5C200] text-[#1a1a1a] hover:bg-[#E6B400] font-semibold rounded-xl border-0 shadow-none transition-colors">
                      Breitensport anfragen
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </Button>
                  </Link>
                </div>
              </FadeIn>
            </TabsContent>

            {/* Youth Tab */}
            <TabsContent value="jugend">
              <FadeIn>
                <div className="text-center mb-10">
                  <h2 className="text-2xl sm:text-3xl font-bold text-[#1a1a1a] mb-3" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                    Jugend & Kids – Sport von Anfang an
                  </h2>
                  <p className="text-gray-600 max-w-2xl mx-auto">
                    Beim HSC Alswede beginnt der Spaß am Sport schon in der Wiege.
                    Kinder unter 6 Jahren trainieren bei uns völlig kostenlos!
                  </p>
                </div>
              </FadeIn>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {youthOffers.map((offer, i) => (
                  <FadeIn key={i} delay={i * 0.1}>
                    <Card className="sport-card rounded-2xl border border-black/[0.08] bg-white p-6 shadow-[0_10px_30px_rgba(17,17,17,0.05)] h-full relative overflow-hidden hover:shadow-[0_16px_45px_rgba(17,17,17,0.10)] transition-shadow">
                      
                      <div className="absolute left-0 top-0 h-full w-1 bg-[#F5C200] rounded-l-2xl" />

                      <div className="pl-3 flex flex-col h-full">

                        <div className="flex items-start gap-4 mb-4">
                          
                          <div className="w-12 h-12 bg-[#F5C200]/15 rounded-2xl flex items-center justify-center">
                            <offer.icon className="w-6 h-6 text-[#1a1a1a]" />
                          </div>

                          <div className="flex-1">
                            <h3 className="font-bold text-[#1a1a1a]" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                              {offer.name}
                            </h3>

                            <div className="text-xs text-gray-500 mb-1">
                              {offer.age}
                            </div>

                            <p className="text-sm text-gray-600 leading-relaxed">
                              {offer.desc}
                            </p>
                          </div>

                        </div>

                        <div className="mt-auto pt-4 border-t border-gray-100 space-y-2">

                          {offer.location && (
                            <div className="flex items-center gap-2 text-xs text-gray-500">
                              <MapPin className="w-3.5 h-3.5" />
                              {offer.location}
                            </div>
                          )}

                          {offer.time && (
                            <div className="flex items-center gap-2 text-xs text-gray-500">
                              <Clock className="w-3.5 h-3.5" />
                              {offer.time}
                            </div>
                          )}

                          {offer.contactName && (
                            <div className="flex items-center gap-2 text-xs text-gray-700 font-medium">
                              <User className="w-3.5 h-3.5" />
                              {offer.contactName}
                            </div>
                          )}

                          <div className="flex flex-wrap gap-2 pt-2">

                            {offer.contactEmail && (
                              <a
                                href={`mailto:${offer.contactEmail}`}
                                className="inline-flex items-center gap-1 rounded-full bg-[#F5C200]/15 px-3 py-1.5 text-xs font-medium text-[#1a1a1a] hover:bg-[#F5C200]/30 transition-colors"
                              >
                                <Mail className="w-3.5 h-3.5" />
                                E-Mail
                              </a>
                            )}

                            {offer.contactPhone && (
                              <a
                                href={`tel:${offer.contactPhone.replace(/\s+/g, '')}`}
                                className="inline-flex items-center gap-1 rounded-full bg-gray-100 px-3 py-1.5 text-xs font-medium text-[#1a1a1a] hover:bg-gray-200 transition-colors"
                              >
                                <Phone className="w-3.5 h-3.5" />
                                Anrufen
                              </a>
                            )}

                          </div>

                          {offer.highlight && (
                            <Badge className={`text-xs border-0 rounded-full mt-2 ${offer.color}`}>
                              {offer.highlight}
                            </Badge>
                          )}

                        </div>

                      </div>
                    </Card>
                  </FadeIn>
                ))}
              </div>
              <FadeIn>
                <div className="mt-8 p-6 bg-[#F5C200]/10 rounded-2xl border border-[#F5C200]/30">
                  <div className="flex items-start gap-4">
                    <div className="text-3xl">🎁</div>
                    <div>
                      <h3 className="font-bold text-[#1a1a1a] mb-2" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                        Kinder unter 6 Jahren spielen kostenlos!
                      </h3>
                      <p className="text-sm text-gray-700 mb-4">
                        Wir investieren bewusst in die Zukunft und wollen Kindern früh die Freude am Sport vermitteln.
                        Daher können Kinder bis zum vollendeten 6. Lebensjahr beim HSC Alswede beitragsfrei trainieren.
                      </p>
                      <Link to="/kontakt">
                        <Button className="h-10 px-5 bg-[#F5C200] text-[#1a1a1a] hover:bg-[#E6B400] font-semibold rounded-xl border-0 shadow-none transition-colors text-sm">
                          Jetzt anmelden
                          <ArrowRight className="w-4 h-4 ml-2" />
                        </Button>
                      </Link>
                    </div>
                  </div>
                </div>
              </FadeIn>
            </TabsContent>
          </Tabs>
        </div>
      </section>
    </div>
  );
}